import { Stack } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import type { PaymentMethod } from '~/common/enums/payments.enum';
import { FeeType, TransactionType } from '~/common/enums/payments.enum';
import dt from '~/testing';

import { useWithdrawalFormContext } from '../context/withdrawal.context';
import { useWalletBalance } from '../../hooks/useWalletBalance';
import { useProcessingFees } from '../../hooks/useProcessingFees';

import { WithdrawalSummaryItem } from './WithdrawalSummaryItem';

type WithdrawalSummaryProps = {
  paymentMethod: PaymentMethod;
};

export function WithdrawalSummary({ paymentMethod }: WithdrawalSummaryProps) {
  const { t } = useTranslation('payments');

  const form = useWithdrawalFormContext();
  const withdrawalAmount = form.values.withdrawalAmount ?? 0;

  const { data, isLoading: isBalanceLoading } = useWalletBalance();
  const withdrawableBalance = data?.withdrawableBalance ?? 0;

  const {
    processingFee,
    calculateFee,
    isLoading: isFeeLoading,
  } = useProcessingFees({
    paymentMethod,
    transactionType: TransactionType.WITHDRAWAL,
  });

  const serviceFee = calculateFee(withdrawalAmount);
  const remainingBalance = withdrawableBalance - withdrawalAmount;
  const transferAmount = withdrawalAmount - serviceFee;

  const renderServiceFee = () => (
    <WithdrawalSummaryItem
      data-test-id={dt.payments.withdrawal.withdrawalForm.summary.serviceFee}
      label={`${t('withdrawal.summary.serviceFee')}${
        processingFee?.type === FeeType.PERCENTAGE ? ` (${processingFee?.amount}%)` : ''
      }`}
      isLoading={isFeeLoading}
      value={-serviceFee}
    />
  );

  const renderTransferAmount = () => (
    <WithdrawalSummaryItem
      data-test-id={dt.payments.withdrawal.withdrawalForm.summary.transferAmount}
      label={t('withdrawal.summary.transferAmount')}
      isLoading={isFeeLoading}
      value={transferAmount}
      sx={{ fontWeight: 600 }}
    />
  );

  const renderRemainingBalance = () => (
    <WithdrawalSummaryItem
      data-test-id={dt.payments.withdrawal.withdrawalForm.summary.remainingBalance}
      label={t('withdrawal.summary.remainingBalance')}
      isLoading={isBalanceLoading}
      value={remainingBalance}
      sx={{ fontWeight: 600 }}
      c="gray.4"
    />
  );

  return (
    <Stack
      /* eslint-disable-line local-rules/use-theme-tokens */ p={16}
      sx={(theme) => ({
        border: `1px solid ${theme.colors.gray[3]}`,
        borderRadius: theme.radius.md,
      })}
    >
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4}>
        <WithdrawalSummaryItem
          data-test-id={dt.payments.withdrawal.withdrawalForm.summary.withdrawalAmount}
          label={t('withdrawal.summary.withdrawalAmount')}
          value={withdrawalAmount}
        />
        {renderServiceFee()}
        {renderTransferAmount()}
        {renderRemainingBalance()}
      </Stack>
    </Stack>
  );
}
